import { useState, useEffect } from 'react';
import { Hash, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { BACKEND_URL } from '@/config/api';

// Edit Channel Modal
export const EditChannelModal = ({ 
  isOpen, 
  onClose, 
  channel,
  token,
  onUpdate 
}) => {
  const [channelName, setChannelName] = useState('');
  const [channelDesc, setChannelDesc] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (channel) {
      setChannelName(channel.name || '');
      setChannelDesc(channel.description || '');
    }
  }, [channel]);

  const handleUpdate = async () => {
    if (!channelName.trim() || !channel) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/channels/${channel.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: channelName,
          description: channelDesc
        })
      });
      if (response.ok) {
        onUpdate?.(channel.id, channelName, channelDesc);
        onClose();
      }
    } catch (error) {
      console.error('Error updating channel:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Channel</DialogTitle>
          <DialogDescription className="text-white/60">
            Update your channel's name and description.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-channel-name" className="text-white/80">Name</Label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
              <Input
                id="edit-channel-name"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                placeholder="channel-name"
                className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="edit-channel-desc" className="text-white/80">Description</Label>
            <Input
              id="edit-channel-desc"
              value={channelDesc}
              onChange={(e) => setChannelDesc(e.target.value)}
              placeholder="What's this channel about?"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={!channelName.trim() || isLoading}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Manage Channel Members Modal
export const ManageChannelMembersModal = ({ 
  isOpen, 
  onClose, 
  channel 
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash size={18} className="text-white/60" />
            Channel Members
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Manage who has access to #{channel?.name || ''}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-white/50 text-sm text-center">
            Member management coming soon...
          </p>
        </div>
        
        <DialogFooter>
          <Button
            onClick={onClose}
            className="bg-white/10 hover:bg-white/20 text-white"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Delete Channel Modal
export const DeleteChannelModal = ({ 
  isOpen, 
  onClose, 
  channel,
  token,
  onDelete 
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    if (!channel) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/channels/${channel.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        onDelete?.(channel.id);
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to delete channel:', response.status, errorData);
        alert(errorData.detail || 'Failed to delete channel. You may not have permission.');
      }
    } catch (error) {
      console.error('Error deleting channel:', error);
      alert('An error occurred while deleting the channel.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-400 flex items-center gap-2">
            <Trash2 size={18} />
            Delete Channel
          </DialogTitle>
          <DialogDescription className="text-white/60">
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-white font-medium flex items-center gap-2">
              <Hash size={16} className="text-white/60" />
              {channel?.name}
            </p>
            <p className="text-white/50 text-sm mt-1">
              All messages in this channel will be permanently deleted.
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isLoading ? 'Deleting...' : 'Delete Channel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
